import pb from '../lib/pocketbaseClient';

// Widths PocketBase makes resized copies of. They must match the `thumbs` option
// on the file fields (pocketbase/pb_migrations/1793019600_add_thumbs.js). For a
// size it doesn't know PocketBase returns the original file, so these URLs keep
// working before that migration runs, just without the savings.
export const THUMB_WIDTHS = [400, 800, 1200, 1600];

// Uploads are resized to 2560px on the longest edge (see mcp-server).
const ORIGINAL_WIDTH = 2560;

/**
 * Helper to get the full URL of a file stored in PocketBase.
 * @param {Object} record - The PocketBase record object containing the file
 * @param {string} fieldName - The name of the file field (default: 'file')
 * @param {number} [width] - One of THUMB_WIDTHS for a resized copy; omit for the original
 * @returns {string} The full URL to the file, or an empty string if invalid
 */
export function getImageUrl(record, fieldName = 'file', width) {
  if (!record || !record.collectionId || !record.id || !record[fieldName]) {
    return '';
  }

  return pb.files.getURL(record, record[fieldName], width ? { thumb: `${width}x0` } : undefined);
}

// Past 2x pixel density a photo in a grid or card looks no sharper, but on a 3x
// phone the file the browser picks is about twice as big. Scaling `sizes` down
// makes 3x screens fetch what a 2x screen would.
const DENSITY_SCALE = Math.min(1, 2 / (window.devicePixelRatio || 1));

/**
 * `sizes` for grids and cards, capped at 2x density, e.g.
 * capSizes('(min-width: 1024px) 33vw, 50vw').
 */
export function capSizes(sizes) {
  if (DENSITY_SCALE === 1) return sizes;
  return sizes
    .split(',')
    .map((part) => part.trim().replace(/(\S+)$/, `calc($1 * ${DENSITY_SCALE.toFixed(3)})`))
    .join(', ');
}

/**
 * A `srcset` with every resized copy plus the original, so the browser picks
 * the smallest file that is sharp at the size the photo is shown.
 */
export function getImageSrcSet(record, fieldName = 'file') {
  if (!getImageUrl(record, fieldName)) return '';
  return [
    ...THUMB_WIDTHS.map((width) => `${getImageUrl(record, fieldName, width)} ${width}w`),
    `${getImageUrl(record, fieldName)} ${ORIGINAL_WIDTH}w`,
  ].join(', ');
}
